/* ===================================================================
   Retro Future — CRO Optimizations JS
   New file: assets/cro-optimizations.js

   Load via theme.liquid, right before </body>:
   <script src="{{ 'cro-optimizations.js' | asset_url }}" defer></script>

   Tuned to your actual theme:
   - <product-form> web component (Dawn)
   - .product-form__submit ATC button
   - <media-gallery> + .product__media-list gallery
   - <product-info> wrapper
   - Microsoft Clarity smart-event bridge

   No dependencies. ~3.5KB minified.
   =================================================================== */
(function () {
  'use strict';

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.from((r || document).querySelectorAll(s)); }
  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  }
  function safeOn(node, evt, handler, opts) {
    if (!node || typeof node.addEventListener !== 'function') return;
    node.addEventListener(evt, handler, opts || false);
  }
  function safeToggleClass(node, cls, force) {
    if (!node || !node.classList) return;
    node.classList.toggle(cls, force);
  }
  // Public utility namespace — other inline scripts can use these too
  window.RF = window.RF || {};
  window.RF.safeToggleClass = safeToggleClass;
  window.RF.safeOn = safeOn;

  /* -------------------------------------------------------------------
     ERROR SHIM — swallow any remaining classList-on-null errors so they
     don't break downstream listeners. After replacing media-gallery.js
     this should never fire, but it's a safety net.
     ------------------------------------------------------------------- */
  var __rfLoggedBridgeNoise = false;
  window.addEventListener(
    'error',
    function (e) {
      var msg = (e && (e.message || '')) + '';
      var isKnownMetaBridgeNoise =
        msg.indexOf('Java object is gone') > -1 ||
        msg.indexOf('enableDidUserTypeOnKeyboardLogging') > -1 ||
        msg.indexOf('navigationPerformanceLoggerJavascriptInterface') > -1;

      // Only suppress confirmed third-party WebView bridge noise. Never hide real
      // theme errors such as classList/null exceptions — those must stay visible
      // in DevTools so storefront regressions can be diagnosed instead of masked.
      if (isKnownMetaBridgeNoise) {
        if (!__rfLoggedBridgeNoise) {
          console.warn('[RF] Suppressed known in-app browser bridge noise. Source:', e.filename, e.lineno);
          __rfLoggedBridgeNoise = true;
        }
        e.preventDefault();
      }
    },
    true
  );

  /* -------------------------------------------------------------------
     1. STICKY ADD-TO-CART CONTROLLER
     ------------------------------------------------------------------- */
  function initStickyATC() {
    var bar = $('[data-rf-sticky-atc]');
    if (!bar) return;
    if (bar.dataset.rfStickyInitialized === 'true') return;
    bar.dataset.rfStickyInitialized = 'true';

    // Anchor on the real product form's submit button. The sticky bar must NOT
    // appear over the opening image stack; it only becomes eligible after the
    // shopper has actually reached the in-page buying controls once.
    var anchor =
      $('product-form button[name="add"]') ||
      $('.product-form__submit') ||
      $('form[action*="/cart/add"] button[type="submit"]') ||
      $('[name="add"]');

    if (!anchor) return;

    var productForm = $('product-form');
    var btn = $('[data-rf-sticky-atc-btn]', bar);
    var hasSeenBuyingControls = false;

    function setVisible(force) {
      safeToggleClass(bar, 'is-visible', !!force);
    }

    function syncEnabledState() {
      if (!btn) return;
      var isDisabled = anchor.hasAttribute('disabled') || anchor.getAttribute('aria-disabled') === 'true';
      if (isDisabled) {
        btn.setAttribute('disabled', '');
        btn.textContent = (anchor.textContent || '').trim() || 'Sold Out';
      } else {
        btn.removeAttribute('disabled');
        var label = anchor.querySelector('span');
        btn.textContent = (label ? label.textContent : anchor.textContent).trim() || 'Add to Cart';
      }
    }

    // Show/hide via IntersectionObserver. The old logic made the sticky bar
    // visible immediately because the real ATC starts below the fold. That was
    // overlapping the image gallery from page load. Gate it until the real ATC
    // has been seen once, then show it only after it scrolls away.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              hasSeenBuyingControls = true;
              setVisible(false);
              return;
            }
            setVisible(hasSeenBuyingControls);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0 }
      );
      io.observe(anchor);
    } else {
      var ticking = false;
      function check() {
        var rect = anchor.getBoundingClientRect();
        var inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView) hasSeenBuyingControls = true;
        setVisible(hasSeenBuyingControls && !inView);
        ticking = false;
      }
      window.addEventListener(
        'scroll',
        function () {
          if (!ticking) {
            window.requestAnimationFrame(check);
            ticking = true;
          }
        },
        { passive: true }
      );
      check();
    }

    // Sticky button delegates to the real form so cart drawer, variant id,
    // analytics, and Shopify product-form behavior remain unchanged.
    var rfAdding = false;
    safeOn(btn, 'click', function (e) {
      e.preventDefault();
      if (anchor.hasAttribute('disabled') || anchor.getAttribute('aria-disabled') === 'true') {
        var variantBlock = $('.rf-variant-block, variant-radios, variant-selects, .product-form__input');
        if (variantBlock) variantBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (rfAdding) return;
      rfAdding = true;
      safeToggleClass(btn, 'is-adding', true);
      anchor.click();
      setTimeout(function () {
        rfAdding = false;
        safeToggleClass(btn, 'is-adding', false);
      }, 1200);
    });

    if (productForm && 'MutationObserver' in window) {
      var mo = new MutationObserver(syncEnabledState);
      mo.observe(productForm, { attributes: true, subtree: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }
    syncEnabledState();
    setVisible(false);
  }

  /* -------------------------------------------------------------------
     2. PRODUCT GALLERY — DEAD-CLICK GUARD
     The Future Addict Shirt PDP had 165 dead clicks. Adds a safety
     net click handler at the media item level so taps never go nowhere.
     ------------------------------------------------------------------- */
  function initGalleryTapFix() {
    /* RF FIX (rage/dead-click root cause):
       This handler used to bind a click listener to every .product__media-item
       (and every nested [data-media-id] node). On any tap that didn't land
       exactly on the native <button>, it either programmatically fired the
       lightbox (modalBtn.click()) or, as a fallback, ran
       `scrollIntoView({ block: 'center' })` on the NEXT image.

       That fallback is exactly the "extra/unwanted scroll on the product
       photos" users hit: a tap (or the click that fires at the end of a
       swipe) scrolled the page/slider to a different image instead of doing
       nothing, and the broad [data-media-id] selector double-bound nested
       elements. The result read as dead clicks + rage clicks in Clarity.

       The real cause was a broken gallery tap target plus an over-aggressive
       gallery behavior layer. The mobile gallery is now a native horizontal
       swipe rail with no touchmove/preventDefault logic and no vertical nested
       scroll. Image lightbox taps are disabled on mobile to avoid dead/rage
       click zones. Left as an intentional no-op so the boot / section-reload /
       variant-change callers stay valid. */
    return;
  }

  /* -------------------------------------------------------------------
     3. LAZY-LOAD FALLBACK
     Any below-the-fold <img> without loading="lazy" gets it.
     ------------------------------------------------------------------- */
  function initLazyLoadFallback() {
    var imgs = $$('img:not([loading])');
    imgs.forEach(function (img, i) {
      // Skip the first 2 images (likely above the fold / LCP candidates)
      if (i < 2) return;
      img.setAttribute('loading', 'lazy');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
  }

  /* -------------------------------------------------------------------
     4. RF MICROHEAD CART COUNT SYNC
     Root cause: rf-microhead renders its own cart icon (data-rf-cart-icon)
     that is outside Dawn's #cart-icon-bubble. Dawn's product-form.js and
     cart-drawer.js only update #cart-icon-bubble (inside the Dawn header
     which is hidden on PDP/Collection pages). This function bridges the
     gap with two complementary strategies:

     Strategy A — MutationObserver on #cart-icon-bubble:
       Dawn always updates this element server-side via section rendering.
       We watch for DOM changes and mirror the count to #rf-cart-count-bubble.

     Strategy B — pubsub subscribe('cart-update'):
       Dawn's product-form.js publishes cartUpdate with the full cart API
       response including item_count. We read it directly and skip the DOM
       round-trip. This fires faster than the MutationObserver.

     Both strategies call the same updateRFCartCount() function which is
     idempotent and safe to call multiple times with the same value.
     ------------------------------------------------------------------- */

  /**
   * Update the rf-microhead cart count bubble.
   * @param {number} count — new item count (0 hides the bubble)
   */
  function updateRFCartCount(count) {
    var rfIcon = document.querySelector('[data-rf-cart-icon]');
    if (!rfIcon) return;

    var bubble = document.getElementById('rf-cart-count-bubble');

    // Create bubble lazily if missing (e.g. after a theme editor reload)
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.id = 'rf-cart-count-bubble';
      bubble.className = 'cart-count-bubble rf-cart-count-bubble';
      bubble.setAttribute('aria-live', 'polite');
      rfIcon.appendChild(bubble);
    }

    if (!count || count < 1) {
      bubble.style.display = 'none';
      bubble.innerHTML = '';
      return;
    }

    var countText = count < 100 ? String(count) : '99+';
    var hiddenLabel = count === 1 ? '1 item' : countText + ' items';

    bubble.style.display = '';
    bubble.innerHTML =
      '<span aria-hidden="true">' + countText + '</span>' +
      '<span class="visually-hidden">' + hiddenLabel + '</span>';

    // Micro-animation: scale-pop to signal update
    bubble.style.transform = 'scale(1.35)';
    clearTimeout(bubble.__rfAnimTimer);
    bubble.__rfAnimTimer = setTimeout(function () {
      bubble.style.transform = '';
    }, 150);
  }

  var rfCartSyncInitialized = false;
  var rfCartMutationObserver = null;
  var rfCartUpdateUnsubscriber = null;

  function initRFCartCountSync() {
    // This wiring is global. Shopify editor section reloads used to attach a new
    // MutationObserver + pubsub subscription every time, multiplying callbacks.
    if (rfCartSyncInitialized) {
      var existingDawnBubble = document.getElementById('cart-icon-bubble');
      if (existingDawnBubble) {
        var existingCountEl = existingDawnBubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
        updateRFCartCount(existingCountEl ? (parseInt(existingCountEl.textContent, 10) || 0) : 0);
      }
      return;
    }
    rfCartSyncInitialized = true;

    // ── Strategy A: Watch Dawn's #cart-icon-bubble for DOM updates ──
    // Dawn re-renders this element's innerHTML whenever the cart changes.
    // Even on pages where the Dawn header is hidden (display:none), the
    // element is still in the DOM and still gets updated by AJAX.
    var dawnBubble = document.getElementById('cart-icon-bubble');
    if (dawnBubble && 'MutationObserver' in window) {
      rfCartMutationObserver = new MutationObserver(function () {
        // Read count from the visible span inside the bubble
        var countEl = dawnBubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
        var count = countEl ? (parseInt(countEl.textContent, 10) || 0) : 0;
        updateRFCartCount(count);
      });
      rfCartMutationObserver.observe(dawnBubble, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // ── Strategy B: Subscribe to Dawn's cart-update pubsub event ──
    // Fires before the DOM is updated by Strategy A, giving instant feedback.
    // Guard: subscribe/PUB_SUB_EVENTS may not be loaded yet on first tick,
    // so we defer to the next microtask with a fallback setTimeout.
    function trySubscribe() {
      if (typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined') {
        rfCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
          if (data && data.cartData && typeof data.cartData.item_count === 'number') {
            updateRFCartCount(data.cartData.item_count);
          }
        });
        return true;
      }
      return false;
    }

    if (!trySubscribe()) {
      // Dawn's pubsub scripts load before cro-optimizations.js in normal
      // theme flow, but defer+async ordering can vary. Retry once.
      setTimeout(trySubscribe, 500);
    }

    // ── Seed count from current page state ──
    // On first load, seed from the Dawn bubble if it already has a count,
    // so the rf-microhead icon is immediately correct without waiting for
    // a cart action.
    if (dawnBubble) {
      var seedEl = dawnBubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
      if (seedEl && seedEl.textContent.trim()) {
        updateRFCartCount(parseInt(seedEl.textContent, 10) || 0);
      }
    }
  }

  /* -------------------------------------------------------------------
     5. CLARITY EVENT BRIDGE
     Fires custom Clarity events so the audit can track funnel steps
     accurately. Run only if window.clarity is defined.
     ------------------------------------------------------------------- */
  function initClarityBridge() {
    if (typeof window.clarity !== 'function') return;

    try {
      if (window.RF && window.RF.flags && window.RF.flags.isMetaWebView) {
        window.clarity('set', 'source_webview', 'meta_in_app');
        window.clarity('event', 'meta_in_app_session');
      } else if (window.RF && window.RF.flags && window.RF.flags.isInAppWebView) {
        window.clarity('set', 'source_webview', 'third_party_in_app');
        window.clarity('event', 'third_party_in_app_session');
      }
    } catch (err) {}

    // Tag PDP variant changes
    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t) return;
        if (t.matches && t.matches('variant-radios input, variant-selects select, [name="id"]')) {
          try {
            window.clarity('event', 'variant_changed');
          } catch (err) {}
        }
      },
      true
    );

    // Tag ATC clicks
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest && e.target.closest('button[name="add"], [name="add"], .product-form__submit, [data-rf-sticky-atc-btn]');
        if (!btn) return;
        try {
          var fromSticky = !!btn.matches('[data-rf-sticky-atc-btn]');
          window.clarity('event', fromSticky ? 'add_to_cart_sticky' : 'add_to_cart_click');
          window.clarity('set', 'pdp_engagement', 'clicked_atc');
        } catch (err) {}
      },
      true
    );

    // Tag scroll milestones without forcing layout work on every scroll event.
    // A single rAF callback handles a burst of scroll events and the listener is
    // removed after all milestones have been sent.
    var sent = { 25: false, 50: false, 75: false };
    var scrollMilestoneRaf = 0;

    function handleScrollMilestones() {
      if (scrollMilestoneRaf) return;
      scrollMilestoneRaf = window.requestAnimationFrame(function () {
        scrollMilestoneRaf = 0;
        var doc = document.documentElement;
        var total = Math.max(doc.scrollHeight, 1);
        var pct = ((window.scrollY + window.innerHeight) / total) * 100;
        var complete = true;

        Object.keys(sent).forEach(function (k) {
          if (!sent[k] && pct >= +k) {
            sent[k] = true;
            try { window.clarity('event', 'scroll_' + k); } catch (err) {}
          }
          if (!sent[k]) complete = false;
        });

        if (complete) window.removeEventListener('scroll', handleScrollMilestones);
      });
    }

    window.addEventListener('scroll', handleScrollMilestones, { passive: true });
  }

  /* -------------------------------------------------------------------
     BOOT
     ------------------------------------------------------------------- */
  ready(function () {
    initStickyATC();
    initGalleryTapFix();
    initLazyLoadFallback();
    initClarityBridge();
    initRFCartCountSync();
  });

  // Re-init on Shopify theme editor section reload
  document.addEventListener('shopify:section:load', function () {
    initStickyATC();
    initGalleryTapFix();
    initRFCartCountSync();
  });

  // Re-init when product info is re-rendered after a variant change (Dawn)
  document.addEventListener('product-info:loaded', function () {
    initGalleryTapFix();
  });
})();
